import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Truck, CheckCircle, WarningCircle, Info, ArrowSquareOut,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * SamsaraConnectCard.
 *
 * Per-owner API-token connection card. Pilots paste their Samsara token
 * (from Samsara Cloud → Settings → API Tokens) and we validate + store
 * it encrypted server-side.
 *
 * States:
 *   - loading
 *   - not_connected: show input + Connect button
 *   - connected: show org name + Sync Drivers + Disconnect
 */
export default function SamsaraConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/samsara/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async () => {
    const t = token.trim();
    if (!t) {
      toast.error("Paste your Samsara API token first.");
      return;
    }
    setConnecting(true);
    try {
      const { data } = await client.post("/samsara/connect", { api_token: t });
      toast.success(`Connected to ${data.org_name || "Samsara"} ✓`);
      setToken("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Samsara rejected the token. Double-check it's active in Samsara Cloud.",
      );
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Samsara? Your drivers stay imported but HOS + GPS stops updating.")) {
      return;
    }
    try {
      await client.delete("/samsara/disconnect");
      toast.success("Disconnected from Samsara.");
      await load();
    } catch (e) {
      toast.error("Could not disconnect. Try again.");
    }
  };

  const syncDrivers = async () => {
    setSyncing(true);
    try {
      const { data } = await client.post("/samsara/sync-drivers");
      toast.success(
        `Synced ${data.total} drivers · ${data.imported} new, ${data.updated} updated`,
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Sync failed. Check your connection.",
      );
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="samsara-connect-card-loading" className="card-surface p-5">
        <div className="animate-pulse h-16" />
      </div>
    );
  }

  const isConnected = status?.connected;

  return (
    <div data-testid="samsara-connect-card" className="card-surface p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <Truck size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-semibold text-white">
              Samsara ELD
            </div>
            {isConnected ? (
              <span
                data-testid="samsara-connect-status-connected"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wide"
              >
                <CheckCircle size={10} weight="fill" /> Connected
              </span>
            ) : (
              <span
                data-testid="samsara-connect-status-disconnected"
                className="inline-flex items-center gap-1 rounded-full bg-neutral-500/15 text-neutral-400 border border-neutral-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wide"
              >
                Not connected
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-400 mt-1">
            {isConnected ? (
              <>
                Connected to <b className="text-white">{status.org_name}</b>.
                HOS and GPS auto-pull into your dispatch view.
              </>
            ) : (
              <>
                Pull driver Hours-of-Service and live GPS straight from your Samsara fleet
                — no more calling drivers to ask if they can take another load.
              </>
            )}
          </p>
        </div>
      </div>

      {isConnected ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-neutral-400 flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0" />
            <div>
              Connected as <b className="text-white">{status.connected_by || "—"}</b>{" "}
              on {new Date(status.connected_at).toLocaleDateString()}.
              HOS + GPS surfaces on the Dispatch page. Import drivers to link Samsara IDs.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={syncDrivers}
              disabled={syncing}
              data-testid="samsara-sync-drivers-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] hover:bg-[#E04A16] text-black disabled:opacity-50"
            >
              {syncing ? "Syncing…" : "Sync drivers from Samsara"}
            </button>
            <button
              type="button"
              onClick={disconnect}
              data-testid="samsara-disconnect-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs bg-white/[0.04] border border-white/10 text-neutral-300 hover:bg-white/[0.08]"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
              Samsara API token
            </div>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="samsara_api_..."
              data-testid="samsara-connect-token-input"
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#FF5A1F]/50"
            />
          </label>
          <div className="text-xs text-neutral-500 flex items-start gap-1.5">
            <WarningCircle size={12} className="mt-0.5 shrink-0" />
            <div>
              Generate one at{" "}
              <a
                href="https://cloud.samsara.com/settings/api-tokens"
                target="_blank" rel="noopener noreferrer"
                className="text-[#FF5A1F] hover:underline inline-flex items-center gap-1"
              >
                Samsara Cloud → Settings → API Tokens
                <ArrowSquareOut size={10} />
              </a>. We store it encrypted per-account.
            </div>
          </div>
          <button
            type="button"
            onClick={connect}
            disabled={connecting || !token.trim()}
            data-testid="samsara-connect-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] hover:bg-[#E04A16] text-black disabled:opacity-50"
          >
            {connecting ? "Connecting…" : "Connect Samsara"}
          </button>
        </div>
      )}
    </div>
  );
}
